// One page of an already-loaded document at a given CSS width (enlarge-preview-annotate task 7.2; Req 2.11-2.13, 6.9,
// 10.1; design "page rendering"). Renders only from the `LoadedDocument`, never from a URL fetch of its own: PDFs
// through react-pdf `<Page pdf={...}>`, TIFFs decoded with UTIF to a canvas, PNG/JPEG as an `<img>` of the URL the
// document was shown from. `onRendered` fires once the bitmap is on screen, with its displayed CSS size and page, so a
// caller draws annotations only for the page actually shown; `onError` fires when the page cannot be shown.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Page } from 'react-pdf';

import type { PageSize } from '../features/annotations/layout';
import type { LoadedDocument } from './loadedDocument';
import { drawTiffPage } from './tiffPage';

/** Largest canvas area browsers allocate reliably (Safari's cap; pdf.js `maxCanvasPixels` uses the same value). */
export const MAX_CANVAS_PIXELS = 16_777_216;

/**
 * The device pixel ratio to render a `cssWidth × cssHeight` page with: the screen's ratio, lowered so the canvas area
 * stays within `maxPixels`. The Enlarged_Viewer covers the visible region at full resolution with `DetailCanvas`.
 */
export function cappedPixelRatio(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  maxPixels: number = MAX_CANVAS_PIXELS,
): number {
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const area = cssWidth * cssHeight;
  if (!(area > 0) || area * dpr * dpr <= maxPixels) return dpr;
  return Math.sqrt(maxPixels / area);
}

type Shown = Exclude<LoadedDocument, { kind: 'loading' | 'error' }>;

export interface PageImageProps {
  loaded: Shown;
  /** 1-based page. */
  page: number;
  /** Displayed width in CSS px. */
  width: number;
  /** Accessible name of the page image. */
  name: string;
  onRendered: (size: PageSize, page: number) => void;
  onError: () => void;
}

/** Keeps the latest callbacks without making them effect dependencies. */
function useLatest<T>(value: T) {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  });
  return ref;
}

export function PageImage(props: PageImageProps) {
  const { loaded } = props;
  switch (loaded.kind) {
    case 'pdf':
      return <PdfPageImage {...props} loaded={loaded} />;
    case 'tiff':
      return <TiffPageImage {...props} loaded={loaded} />;
    case 'image':
      return <ImagePageImage {...props} loaded={loaded} />;
  }
}

function PdfPageImage({
  loaded,
  page,
  width,
  name,
  onRendered,
  onError,
}: PageImageProps & { loaded: Extract<Shown, { kind: 'pdf' }> }) {
  const cb = useLatest({ onRendered, onError });
  // Height-to-width ratio of the page, read before the first render so the canvas size can be capped up front.
  const [aspect, setAspect] = useState<{ pdf: unknown; page: number; ratio: number } | null>(null);
  useEffect(() => {
    let cancelled = false;
    loaded.pdf.getPage(page).then(
      (p) => {
        if (cancelled) return;
        const v = p.getViewport({ scale: 1 });
        if (!(v.width > 0 && v.height > 0)) {
          cb.current.onError();
          return;
        }
        setAspect({ pdf: loaded.pdf, page, ratio: v.height / v.width });
      },
      () => {
        if (!cancelled) cb.current.onError();
      },
    );
    return () => {
      cancelled = true;
    };
  }, [loaded.pdf, page, cb]);

  if (!aspect || aspect.pdf !== loaded.pdf || aspect.page !== page) return null;
  const dpr = cappedPixelRatio(width, width * aspect.ratio, window.devicePixelRatio || 1);
  return (
    <div role="img" aria-label={name} style={{ width }}>
      <Page
        pdf={loaded.pdf}
        pageNumber={page}
        width={width}
        devicePixelRatio={dpr}
        renderAnnotationLayer={false}
        renderTextLayer={false}
        loading={null}
        error={null}
        noData={null}
        onRenderSuccess={(p) => cb.current.onRendered({ width: p.width, height: p.height }, page)}
        onRenderError={() => cb.current.onError()}
        onLoadError={() => cb.current.onError()}
      />
    </div>
  );
}

function TiffPageImage({
  loaded,
  page,
  width,
  name,
  onRendered,
  onError,
}: PageImageProps & { loaded: Extract<Shown, { kind: 'tiff' }> }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const cb = useLatest({ onRendered, onError });
  // The page last drawn to the canvas, so a size is reported only for the page actually shown.
  const [drawn, setDrawn] = useState<{ tiff: unknown; page: number; ratio: number } | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    try {
      const px = drawTiffPage(loaded.tiff, page, canvas);
      setDrawn({ tiff: loaded.tiff, page, ratio: px.height / px.width });
    } catch {
      setDrawn(null);
      cb.current.onError();
    }
  }, [loaded.tiff, page, cb]);
  const ratio = drawn && drawn.tiff === loaded.tiff && drawn.page === page ? drawn.ratio : null;
  // Reported after the canvas is sized with CSS, so the size matches what is on screen.
  useEffect(() => {
    if (ratio !== null) cb.current.onRendered({ width, height: width * ratio }, page);
  }, [ratio, width, page, cb]);
  return (
    <canvas
      ref={ref}
      role="img"
      aria-label={name}
      style={{ display: 'block', width, height: ratio === null ? 'auto' : width * ratio }}
    />
  );
}

function ImagePageImage({
  loaded,
  page,
  width,
  name,
  onRendered,
  onError,
}: PageImageProps & { loaded: Extract<Shown, { kind: 'image' }> }) {
  const cb = useLatest({ onRendered, onError });
  const [loadedRatio, setRatio] = useState<{ url: string; ratio: number } | null>(null);
  const ratio = loadedRatio?.url === loaded.url ? loadedRatio.ratio : null;
  const missing = page !== 1;
  useEffect(() => {
    if (missing) cb.current.onError();
  }, [missing, cb]);
  useEffect(() => {
    if (!missing && ratio !== null) cb.current.onRendered({ width, height: width * ratio }, page);
  }, [missing, ratio, width, page, cb]);
  if (missing) return null;
  return (
    <img
      src={loaded.url}
      alt={name}
      width={width}
      style={{ display: 'block', width, height: 'auto' }}
      onLoad={(e) => {
        const img = e.currentTarget;
        if (img.naturalWidth > 0 && img.naturalHeight > 0)
          setRatio({ url: loaded.url, ratio: img.naturalHeight / img.naturalWidth });
        else cb.current.onError();
      }}
      onError={() => cb.current.onError()}
    />
  );
}
